import React from 'react';
import { useV2Theme, type V2Theme } from '../../providers/theme/ThemeProvider';

export type V2ThemeToggleMode = 'simple' | 'full';

export interface V2ThemeToggleProps {
    /** 'simple' = light/dark toggle only, 'full' = shows all theme options */
    mode?: V2ThemeToggleMode;
    /** Compact size for header usage */
    compact?: boolean;
    /** Additional className */
    className?: string;
}

/* ─── Icons ─── */

const SunIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
);

const MoonIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
);

const MonitorIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
);

/* ─── Full Theme Options ─── */

const THEME_OPTIONS: Array<{ value: V2Theme; label: string; icon: React.FC<{ size?: number }> }> = [
    { value: 'light', label: 'Light', icon: SunIcon },
    { value: 'dark', label: 'Dark', icon: MoonIcon },
    { value: 'system', label: 'System', icon: MonitorIcon },
];

/* ─── Component ─── */

const V2ThemeToggle: React.FC<V2ThemeToggleProps> = ({
    mode = 'simple',
    compact = false,
    className = '',
}) => {
    const { theme, isDark, setTheme, toggleTheme } = useV2Theme();

    const btnSize = compact ? 36 : 40;
    const iconSize = compact ? 16 : 18;

    /* ── Simple toggle (cycles system → light → dark) ── */
    if (mode === 'simple') {
        const CurrentIcon =
            theme === 'system' ? MonitorIcon :
                theme === 'dark' ? MoonIcon :
                    SunIcon;

        const nextLabel =
            theme === 'system' ? 'Switch to light mode' :
                theme === 'light' ? 'Switch to dark mode' :
                    'Switch to system mode';

        return (
            <button
                type="button"
                onClick={toggleTheme}
                aria-label={nextLabel}
                className={className}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: btnSize,
                    height: btnSize,
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-glass)',
                    background: 'var(--bg-glass)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all var(--duration-fast) var(--ease-calm)',
                }}
                onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--bg-glass-strong)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--bg-glass)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                }}
            >
                <CurrentIcon size={iconSize} />
            </button>
        );
    }

    /* ── Full theme picker ── */
    return (
        <div
            className={className}
            style={{
                display: 'inline-flex',
                gap: 4,
                padding: 4,
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-glass)',
                border: '1px solid var(--border-glass)',
            }}
            role="radiogroup"
            aria-label="Theme selection"
        >
            {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
                const isActive = theme === value;
                return (
                    <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        aria-label={`${label} theme`}
                        onClick={() => setTheme(value)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            padding: compact ? '6px 10px' : '8px 14px',
                            borderRadius: 'var(--radius-sm)',
                            border: 'none',
                            background: isActive ? 'var(--primary-100)' : 'transparent',
                            color: isActive ? 'var(--primary-500)' : 'var(--text-muted)',
                            fontSize: 'var(--font-caption)',
                            fontWeight: isActive ? 600 : 400,
                            cursor: 'pointer',
                            transition: 'all var(--duration-fast) var(--ease-calm)',
                        }}
                        onMouseEnter={(e) => {
                            if (!isActive) {
                                (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
                                (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isActive) {
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                                (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
                            }
                        }}
                    >
                        <Icon size={compact ? 14 : 16} />
                        {!compact && <span>{label}</span>}
                    </button>
                );
            })}
        </div>
    );
};

export default V2ThemeToggle;
